<template>
  <v-container fluid>
    <v-btn text @click="$router.back()">
      <v-icon left>mdi-arrow-left</v-icon> {{ $t("back") }}
    </v-btn>

    <h2 class="font-weight-bold my-4">{{ $t("update_product") }}</h2>

    <v-form ref="form" v-model="valid" lazy-validation>
      <v-row dense>
        <v-col cols="12">
          <v-text-field
            v-model="form.name"
            :label="$t('product_name')"
            :rules="[rules.required]"
            outlined
            dense
          />
        </v-col>

        <v-col cols="12">
          <v-text-field
            v-model="form.box_barcode"
            :label="$t('box_barcode')"
            persistent-hint
            outlined
            dense
          />
        </v-col>

        <v-col cols="12">
          <v-text-field
            v-model="form.barcode"
            :label="$t('product_barcode')"
            :rules="[rules.required]"
            persistent-hint
            outlined
            dense
          />
        </v-col>

        <v-col cols="12">
          <v-text-field
            v-model="form.day_before_expiry"
            :label="$t('day_before_expiry')"
            type="number"
            min="0"
            outlined
            dense
          />
        </v-col>

        <v-col cols="12">
          <v-select
            v-model="form.unit"
            :items="unitOptions"
            :label="$t('unit')"
            :rules="[rules.required]"
            persistent-hint
            outlined
            disabled
            dense
          />
        </v-col>

        <template v-if="form.unit.toLowerCase() === 'quantity'">
          <v-col cols="12">
            <v-text-field
              v-model="form.quantity_per_box"
              :label="$t('products_per_box')"
              type="number"
              min="0"
              outlined
              dense
            />
          </v-col>
        </template>

        <v-col cols="12">
          <v-autocomplete
            v-model="form.sub_category_id"
            :items="subcategories"
            :loading="loadingSubcategories"
            :search-input.sync="subcategorySearch"
            item-text="name"
            item-value="id"
            :label="$t('subcategory')"
            :rules="[rules.required]"
            outlined
            dense
            clearable
          />
        </v-col>

        <v-col cols="12">
          <v-text-field
            v-model="form.stock_management"
            :label="$t('stock_notification')"
            type="number"
            min="1"
            outlined
            dense
          />
        </v-col>

        <v-col cols="12">
          <div>
            <label class="font-weight-medium">
              {{ $t("selling_price_per_multiple_products") }}
            </label>

            <v-row dense class="mt-1">
              <v-col cols="3">
                <v-text-field
                  v-model="form.selling_price_multiple"
                  :label="$t('selling_price')"
                  type="number"
                  min="0"
                  outlined
                  dense
                />
              </v-col>

              <v-col
                cols="1"
                class="d-flex align-center justify-center font-weight-medium"
                style="margin-top: -8px"
              >
                {{ $t("per") }}
              </v-col>

              <v-col cols="3">
                <v-text-field
                  v-model="form.selling_price_multiple_count"
                  :label="$t('number_of_products')"
                  type="number"
                  min="1"
                  outlined
                  dense
                />
              </v-col>
            </v-row>
          </div>
        </v-col>

        <v-col cols="12" md="6">
          <v-text-field
            v-model="form.box_wholesale_price"
            :label="$t('box_wholesale_price')"
            type="number"
            min="0"
            step="0.01"
            outlined
            dense
          />
        </v-col>

        <v-col cols="12" md="6">
          <v-text-field
            v-model="form.box_half_wholesale_price"
            :label="$t('box_half_wholesale_price')"
            type="number"
            min="0"
            step="0.01"
            outlined
            dense
          />
        </v-col>

        <v-col cols="12" md="6">
          <v-text-field
            v-model="form.item_wholesale_price"
            :label="$t('item_wholesale_price')"
            type="number"
            min="0"
            step="0.01"
            outlined
            dense
          />
        </v-col>

        <v-col cols="12" md="6">
          <v-text-field
            v-model="form.item_half_wholesale_price"
            :label="$t('item_half_wholesale_price')"
            type="number"
            min="0"
            step="0.01"
            outlined
            dense
          />
        </v-col>

        <v-col cols="12">
          <v-btn color="amber darken-2" :disabled="!valid" @click="submitForm">
            {{ $t("update") }}
          </v-btn>
        </v-col>
      </v-row>
    </v-form>
  </v-container>
</template>

<script>
import api from "@/services/axios"; // your axios wrapper

export default {
  data() {
    return {
      prevFilter: null,
      valid: false,
      unitOptions: ["Quantity", "KG"],
      productList: [],
      subcategories: [],
      subcategorySearch: "",
      loadingSubcategories: false,
      subcategorySearchTimeout: null,

      form: {
        name: "",
        barcode: "",
        unit: "",
        sub_category_id: "",
        box_barcode: "",
        day_before_expiry: null,
        quantity_per_box: null,
        selling_price_multiple: null,
        selling_price_multiple_count: null,
        stock_management: null,
        box_wholesale_price: null,
        box_half_wholesale_price: null,
        item_wholesale_price: null,
        item_half_wholesale_price: null,
      },

      rules: {
        required: (v) => !!v || this.$t("field_required"),
      },
    };
  },

  mounted() {
    if (this.$route.params.id) {
      this.fetchDetails();
    }
  },

  watch: {
    subcategorySearch(val) {
      clearTimeout(this.subcategorySearchTimeout);
      if (val && val.length >= 1) {
        this.subcategorySearchTimeout = setTimeout(() => {
          this.searchSubcategories(val);
        }, 300);
      }
    },
  },
  methods: {
    async fetchDetails() {
      try {
        const res = await api.get(`/product/view/${this.$route.params.id}`);
        const data = res.data.data;
        this.form.name = data.name;
        this.form.barcode = data.barcode;
        this.form.box_barcode = data.box_barcode;
        this.form.day_before_expiry = data.day_before_expiry;
        this.form.sub_category_id = data.subcategory.id;
        this.form.quantity_per_box = data.quantity_per_box;
        this.form.box_wholesale_price = data.box_wholesale_price;
        this.form.box_half_wholesale_price = data.box_half_wholesale_price;
        this.form.item_wholesale_price = data.item_wholesale_price;
        this.form.item_half_wholesale_price = data.item_half_wholesale_price;
        this.form.unit = data.unit == "kg" ? "KG" : "Quantity";
        this.form.product_id = data.id;
        if (data.selling_price_multiple) {
          this.form.selling_price_multiple = data.selling_price_multiple;
        }
        if (data.selling_price_multiple_count) {
          this.form.selling_price_multiple_count =
            data.selling_price_multiple_count;
        }
        if (data.stock_management) {
          this.form.stock_management = data.stock_management;
        }
        this.searchSubcategories(data.subcategory.name);
        this.subcategorySearch = data.subcategory.name;
      } catch (err) {
        console.error("Error fetching product details:", err);
      }
    },

    async searchSubcategories(filter) {
      if (this.prevFilter === filter) {
        return;
      }
      this.loadingSubcategories = true;
      try {
        const res = await api.get("/category/sub-category/list", {
          params: { filter: filter },
        });
        this.subcategories = res.data.data;
      } catch (error) {
        console.error("Subcategory fetch failed", error);
        this.subcategories = [];
      } finally {
        this.loadingSubcategories = false;
      }
    },

    async submitForm() {
      if (!this.$refs.form.validate()) return;

      const formData = new FormData();
      if (this.form.name) formData.append("name", this.form.name);
      if (this.form.barcode) formData.append("barcode", this.form.barcode);
      if (this.form.selling_price_multiple) {
        formData.append(
          "selling_price_multiple",
          this.form.selling_price_multiple
        );
      }
      if (this.form.selling_price_multiple_count) {
        formData.append(
          "selling_price_multiple_count",
          this.form.selling_price_multiple_count
        );
      }
      if (this.form.box_barcode)
        formData.append("box_barcode", this.form.box_barcode);
      if (this.form.day_before_expiry)
        formData.append("day_before_expiry", this.form.day_before_expiry);
      if (this.form.unit.toLowerCase() === "quantity") {
        if (this.form.quantity_per_box)
          formData.append("quantity_per_box", this.form.quantity_per_box);
      }
      if (this.form.unit) formData.append("unit", this.form.unit.toLowerCase());
      formData.append("sub_category_id", this.form.sub_category_id);
      if (this.form.stock_management)
        formData.append("stock_management", this.form.stock_management);
      if (this.form.box_wholesale_price)
        formData.append("box_wholesale_price", this.form.box_wholesale_price);
      if (this.form.box_half_wholesale_price)
        formData.append(
          "box_half_wholesale_price",
          this.form.box_half_wholesale_price
        );
      if (this.form.item_wholesale_price)
        formData.append("item_wholesale_price", this.form.item_wholesale_price);
      if (this.form.item_half_wholesale_price)
        formData.append(
          "item_half_wholesale_price",
          this.form.item_half_wholesale_price
        );
      try {
        const res = await api.post(
          "/product/update/" + this.$route.params.id,
          formData
        );
        if (!res.data.status) {
          this.$root.$emit("show-snackbar", {
            message: this.$te(res.data.locale_key)
              ? res.data.locale_key
              : res.data.message,
            color: "error",
          });
        } else {
          this.$root.$emit("show-snackbar", {
            message: this.$te(res.data.locale_key)
              ? res.data.locale_key
              : res.data.message,
            color: "success",
          });
          this.$router.back();
        }
      } catch (err) {
        console.error(err);
      }
    },
  },
};
</script>
